import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Camera, MapPin, Clock, CheckCircle2, AlertTriangle, LogIn, LogOut, ScanFace, RefreshCw, BarChart3, MessageSquareText, Wallet, CalendarCheck, Coffee } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card, PageHeader, Button, Badge } from "@/components/ui-kit";
import { loadFaceApi, getCamera, stopStream, detectFace, detectBlink, snapshotVideo, IS_LOW_END } from "@/lib/faceApi";
import { speakVoice, VOICE_PHRASES, warmupVoices } from "@/lib/voice";

async function getGPS() {
  // Iter v34j (#4) — Multi-sample (3x) untuk akurasi lebih baik. Pilih sample dengan accuracy terkecil.
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) return reject(new Error("Browser tidak mendukung GPS"));
    let best = null;
    let samples = 0;
    const want = 3;
    const opts = { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 };
    const takeSample = () => {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          if (!best || pos.coords.accuracy < best.accuracy) best = pos.coords;
          samples += 1;
          if (samples < want) setTimeout(takeSample, 600);
          else resolve({ latitude: best.latitude, longitude: best.longitude, accuracy: best.accuracy });
        },
        (err) => {
          // Kalau sudah ada minimal 1 sample yang berhasil, pakai itu
          if (best) resolve({ latitude: best.latitude, longitude: best.longitude, accuracy: best.accuracy });
          else reject(err);
        },
        opts,
      );
    };
    takeSample();
  });
}

export default function KaryawanAbsen() {
  const { user } = useAuth();
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [stream, setStream] = useState(null);
  const [busy, setBusy] = useState(false);
  const [office, setOffice] = useState(null);
  const [summary, setSummary] = useState(null);
  const [profile, setProfile] = useState(null); // Iter v34d (#9) — needed for allow_debt flag
  const [holidays, setHolidays] = useState([]);  // Iter v34n (#1) — libur nasional bulan ini
  const [faReady, setFaReady] = useState(false);
  const [faError, setFaError] = useState(null);
  const [statusMsg, setStatusMsg] = useState("");
  // Iter v28 Phase 3 — face-failure retry state per kind (in/out). Reset on success/page reload.
  const [faceFailState, setFaceFailState] = useState({ in: null, out: null });
  // Iter v34al — Cooldown timer setelah clock-in untuk mencegah double-tap
  // yang tak sengaja langsung clock-out. Countdown 30 menit.
  // Iter v34al — Cooldown timer setelah clock-in untuk mencegah double-tap
  // yang tak sengaja langsung clock-out. Countdown 30 menit.
  // Iter v34ce (#4) — Persist cooldown ke localStorage agar tidak hilang saat
  // refresh. Kunci per-user (email) supaya tidak bocor antar akun.
  // Iter v34cf — Hydrate hanya setelah user.email tersedia, agar tidak menghapus
  // key yang benar karena state=0 saat render awal user belum loaded.
  const _cooldownKey = user?.email ? `absen_cooldown_${user.email}` : null;
  const [cooldownUntil, setCooldownUntil] = useState(0);
  const [cooldownHydrated, setCooldownHydrated] = useState(false);
  const [nowTick, setNowTick] = useState(Date.now());
  // Hydrate cooldown dari localStorage setelah user.email tersedia.
  useEffect(() => {
    if (!_cooldownKey) return;
    try {
      const v = parseInt(localStorage.getItem(_cooldownKey) || "0", 10);
      setCooldownUntil(v > Date.now() ? v : 0);
    } catch { /* ignore */ }
    setCooldownHydrated(true);
  }, [_cooldownKey]);
  // Simpan cooldown expiry ke localStorage tiap kali berubah (setelah hydrated).
  useEffect(() => {
    if (!_cooldownKey || !cooldownHydrated) return;
    try {
      if (cooldownUntil > Date.now()) localStorage.setItem(_cooldownKey, String(cooldownUntil));
      else localStorage.removeItem(_cooldownKey);
    } catch { /* ignore */ }
  }, [cooldownUntil, _cooldownKey, cooldownHydrated]);
  useEffect(() => {
    if (cooldownUntil <= Date.now()) return;
    const id = setInterval(() => setNowTick(Date.now()), 1000);
    return () => clearInterval(id);
  }, [cooldownUntil]);
  // Iter v34cj — warmup voices supaya TTS siap saat absen
  useEffect(() => { warmupVoices(); }, []);
  const cooldownRemaining = Math.max(0, Math.ceil((cooldownUntil - nowTick) / 1000));
  // Iter v34bq — Cooldown 30 menit → tampilkan format menit bila > 60 detik.
  const cooldownDisplay = cooldownRemaining > 60
    ? `${Math.floor(cooldownRemaining / 60)} menit ${cooldownRemaining % 60} detik`
    : `${cooldownRemaining} detik`;

  // Iter v34ao (#2) — Break/Istirahat: derive UI state from `summary.today.breaks`.
  const today = summary?.today || null;
  const breakConfig = summary?.break_config || { enabled: true, min_hours_after_clockin: 8, max_breaks_per_day: 0 };
  const activeBreak = (today?.breaks || []).find((b) => b.start_ts && !b.end_ts) || null;
  const onBreak = !!activeBreak;
  const hoursSinceClockIn = today?.clock_in_ts
    ? Math.max(0, (nowTick - new Date(today.clock_in_ts).getTime()) / 3600000)
    : 0;
  useEffect(() => {
    // Keep nowTick fresh so the 8h threshold reveals the Istirahat button live.
    if (!today?.clock_in_ts || today?.clock_out_ts) return;
    const id = setInterval(() => setNowTick(Date.now()), 30000);
    return () => clearInterval(id);
  }, [today?.clock_in_ts, today?.clock_out_ts]);
  const canShowBreakBtn =
    !!today?.clock_in_ts &&
    !today?.clock_out_ts &&
    breakConfig.enabled &&
    hoursSinceClockIn >= (breakConfig.min_hours_after_clockin || 8);
  const breaksTakenCount = (today?.breaks || []).length;
  const breakLimitReached =
    (breakConfig.max_breaks_per_day || 0) > 0 && breaksTakenCount >= (breakConfig.max_breaks_per_day || 0);

  const doBreakAction = async (action) => {
    // action: "start" | "end"
    const verb = action === "start" ? "Mulai Istirahat" : "Lanjut Bekerja";
    if (!window.confirm(`${verb} sekarang?`)) return;
    setBusy(true);
    try {
      await api.post(`/attendance/break/${action}`, { note: "" });
      toast.success(action === "start" ? "Istirahat dimulai" : "Selamat bekerja kembali!");
      await loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal");
    } finally {
      setBusy(false);
    }
  };

  const loadAll = async () => {
    // Iter v34 — Promise.allSettled agar 1 endpoint gagal tidak mereset state lain
    const [oRes, sRes, pRes, hRes] = await Promise.allSettled([
      api.get("/office-location"),
      api.get("/attendance/me/summary"),
      api.get("/employees/me/profile"),
      api.get(`/national-holidays?year=${new Date().getFullYear()}`),
    ]);
    if (oRes.status === "fulfilled") setOffice(oRes.value.data);
    if (sRes.status === "fulfilled") {
      setSummary(sRes.value.data);
    } else {
      const detail = sRes.reason?.response?.data?.detail || "";
      if (detail.includes("verifikasi") || detail.includes("non-aktif")) {
        setSummary({ blocked: true, message: detail });
      }
    }
    // Iter v34q (#3) — Profile_completed gate sekarang ditangani Layout.jsx (global).
    if (pRes.status === "fulfilled" && pRes.value.data) {
      setProfile(pRes.value.data);
    }
    if (hRes.status === "fulfilled") {
      setHolidays((hRes.value.data?.rows || []));
    }
  };

  // Iter v30 — same-origin model loader with retry capability + progress messages
  const initFaceApi = async () => {
    setFaError(null); setFaReady(false);
    try {
      await loadFaceApi((msg) => setStatusMsg(msg));
      setFaReady(true); setStatusMsg("");
    } catch (e) {
      setFaError(e.message || "Gagal load face-api");
      setStatusMsg("");
    }
  };

  useEffect(() => { loadAll(); initFaceApi(); }, []);
  useEffect(() => () => { stopStream(stream); }, [stream]);

  const startCam = async () => {
    try {
      const s = await getCamera(videoRef.current, { facingMode: "user" });
      setStream(s);
    } catch (e) { toast.error(e.message); }
  };

  const stopCam = () => { stopStream(stream); setStream(null); };

  const captureDescriptor = async () => {
    if (!faReady) throw new Error("Face model belum siap. Tap 'Coba muat ulang model'.");
    // Iter v34ap — Adaptive baseline blink detection. UI menampilkan Δ EAR
    // realtime supaya karyawan tahu apakah kedipan mereka cukup dalam.
    const livenessMs = IS_LOW_END ? 9000 : 8000;
    setStatusMsg(`LIVENESS: KEDIPKAN MATA (${Math.round(livenessMs / 1000)} detik) — kedipkan agak lebar!`);
    const live = await detectBlink(videoRef.current, {
      timeoutMs: livenessMs,
      onTick: ({ noFace }) => {
        if (noFace) setStatusMsg("Sedang proses");
        else setStatusMsg("Kedipkan mata");
      },
    });
    if (!live.ok) {
      const err = new Error("Gagal memproses");
      err.livenessFailed = true;
      throw err;
    }
    setStatusMsg(live.fallback ? "Liveness OK (fallback)..." : "Wajah terverifikasi hidup, capturing...");
    const result = await detectFace(videoRef.current);
    if (!result) throw new Error("Wajah tidak terdeteksi — coba lebih dekat, pencahayaan lebih terang");
    const selfie = snapshotVideo(videoRef.current, canvasRef.current, 0.6);
    return { descriptor: result.descriptor, selfie };
  };

  const setupFace = async () => {
    setBusy(true); setStatusMsg("Memindai wajah...");
    try {
      const { descriptor, selfie } = await captureDescriptor();
      await api.post("/employees/me/face", { face_descriptor: descriptor, face_photo_b64: selfie });
      toast.success("Wajah berhasil disimpan!"); loadAll();
    } catch (e) { toast.error(e.message || "Gagal setup wajah"); } finally { setBusy(false); setStatusMsg(""); }
  };


  const doClock = async (type, force_override = false) => {
    // Iter v34al — Anti double-tap: konfirmasi clock-out & block dalam cooldown.
    // Iter v34bq — Cooldown diperpanjang jadi 30 menit (dari 10 detik).
    if (type === "out" && cooldownUntil > Date.now()) {
      toast.error(`Tunggu ${cooldownDisplay} sebelum clock-out (jeda minimal setelah clock-in).`);
      return;
    }
    if (type === "out" && !force_override) {
      if (!window.confirm("Konfirmasi Clock-Out — Anda yakin ingin mengakhiri jam kerja sekarang?")) return;
    }
    setBusy(true); setStatusMsg(`Memproses ${type}...`);
    try {
      setStatusMsg("Mengambil lokasi GPS...");
      const gps = await getGPS();
      setStatusMsg("Memindai wajah...");
      const { descriptor, selfie } = await captureDescriptor();
      setStatusMsg(`Kirim ${type}...`);
      const path = type === "in" ? "clock-in" : "clock-out";
      const r = await api.post(`/attendance/${path}`, {
        latitude: gps.latitude, longitude: gps.longitude, accuracy_meters: gps.accuracy,
        face_descriptor: descriptor, selfie_photo_b64: selfie, force_override,
      });
      const inside = type === "in" ? r.data.clock_in_inside_radius : r.data.clock_out_inside_radius;
      const overrideUsed = type === "in" ? r.data.clock_in_face_override : r.data.clock_out_face_override;
      let msg = `${type === "in" ? "Clock-in" : "Clock-out"} berhasil!`;
      if (overrideUsed) msg += " ⚠ Override wajah — menunggu persetujuan";
      else if (!inside) msg += " ⚠ Di luar radius — menunggu persetujuan";
      else msg += " ✓ Dalam radius kantor";
      toast.success(msg);
      // Iter v34cj — Voice notification untuk hasil sukses clock-in/out
      if (type === "in") speakVoice(VOICE_PHRASES.granted);
      else speakVoice(VOICE_PHRASES.thankyou);
      setFaceFailState((s) => ({ ...s, [type]: null }));
      // Iter v34al — Setelah clock-in sukses, aktifkan cooldown clock-out.
      // Iter v34bq — Diperpanjang jadi 30 menit (dari 10 detik) sesuai permintaan
      // agar tidak salah tekan clock-out setelah baru saja clock-in.
      if (type === "in") {
        setCooldownUntil(Date.now() + 30 * 60 * 1000); // 30 menit
        setNowTick(Date.now());
      }
      loadAll();
    } catch (e) {
      // Iter v28 Phase 3 — face verification soft-failure (422)
      const data = e.response?.data;
      if (e.response?.status === 422 && data?.face_failed) {
        setFaceFailState((s) => ({ ...s, [type]: { attempts: data.attempts, max: data.max_attempts, canOverride: data.can_override, dist: data.face_distance } }));
        toast.error(data.detail || `Verifikasi wajah gagal (${data.attempts}/${data.max_attempts})`);
        // Iter v34cj — Voice "Access denied" untuk face verification gagal
        speakVoice(VOICE_PHRASES.denied);
      } else if (e.response?.status === 400 && /sudah.*clock.?in|already/i.test(data?.detail || "")) {
        // Iter v34cj — "You have already clocked in"
        speakVoice(VOICE_PHRASES.already);
        toast.error(data?.detail || "Anda sudah clock-in hari ini");
      } else {
        toast.error(data?.detail || e.message || "Gagal absen");
        speakVoice(VOICE_PHRASES.denied);
      }
    }
    finally { setBusy(false); setStatusMsg(""); }
  };

  const needsFace = summary && !summary.has_face_setup && !summary.blocked;
  const blocked = summary?.blocked;

  return (
    <div className="min-h-full bg-[#f6f5f0]">
    <div className="max-w-4xl mx-auto p-4 md:p-6 lg:p-8 space-y-6" data-testid="karyawan-absen-page">
      <PageHeader overline={`Karyawan · ${user?.name}`} title="Absensi Hari Ini"
        description="Wajib menyalakan GPS dan kamera. Pastikan Anda berada di dalam radius kantor." />

      {/* Iter v31 — Karyawan portal navigation */}
      <div className="flex flex-wrap gap-2" data-testid="karyawan-nav">
        <Link to="/karyawan/absen" className="inline-flex items-center gap-1.5 px-4 py-2 text-xs rounded-full bg-stone-900 text-white">
          <Camera className="h-3.5 w-3.5" /> Absen Hari Ini
        </Link>
        <Link to="/karyawan/ringkasan" className="inline-flex items-center gap-1.5 px-4 py-2 text-xs rounded-full bg-white border border-stone-300 text-stone-700 hover:border-stone-400 hover:bg-stone-50" data-testid="nav-karyawan-ringkasan">
          <BarChart3 className="h-3.5 w-3.5" /> Ringkasan Saya
        </Link>
        <Link to="/karyawan/chat-hr" className="inline-flex items-center gap-1.5 px-4 py-2 text-xs rounded-full bg-white border border-stone-300 text-stone-700 hover:border-stone-400 hover:bg-stone-50" data-testid="nav-karyawan-chat-hr">
          <MessageSquareText className="h-3.5 w-3.5" /> Chat HR
        </Link>
        {/* Iter v34e (#1) — Always accessible (kuota cuti universal) */}
        <Link to="/karyawan/cuti" className="inline-flex items-center gap-1.5 px-4 py-2 text-xs rounded-full bg-white border border-stone-300 text-stone-700 hover:border-stone-400 hover:bg-stone-50" data-testid="nav-karyawan-cuti">
          <CalendarCheck className="h-3.5 w-3.5" /> Cuti / Sakit
        </Link>
        {/* Iter v34d (#9) — Kasbon link only when admin has enabled allow_debt for this karyawan */}
        {profile?.allow_debt && (
          <Link to="/karyawan/kasbon" className="inline-flex items-center gap-1.5 px-4 py-2 text-xs rounded-full bg-white border border-stone-300 text-stone-700 hover:border-stone-400 hover:bg-stone-50" data-testid="nav-karyawan-kasbon">
            <Wallet className="h-3.5 w-3.5" /> Kasbon Saya
          </Link>
        )}
      </div>

      {blocked && (
        <Card className="p-4 bg-amber-50 border-amber-300" data-testid="absen-blocked-banner">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-amber-700 mt-0.5" />
            <div className="flex-1">
              <h4 className="font-semibold text-amber-900 text-sm">Akun Belum Diverifikasi</h4>
              <p className="text-xs text-amber-700 mt-1">{summary?.message || "Akun Anda menunggu verifikasi HR. Hubungi admin untuk diaktifkan."}</p>
            </div>
          </div>
        </Card>
      )}

      {!blocked && needsFace && (
        <Card className="p-4 bg-amber-50 border-amber-300">
          <div className="flex items-start gap-3">
            <ScanFace className="h-5 w-5 text-amber-700 mt-0.5" />
            <div className="flex-1">
              <h4 className="font-semibold text-amber-900 text-sm">Setup Wajah Terlebih Dahulu</h4>
              <p className="text-xs text-amber-700 mt-1">Anda belum mendaftarkan wajah untuk verifikasi absensi. Nyalakan kamera, hadapi lensa, lalu klik &ldquo;Simpan Wajah&rdquo;.</p>
            </div>
          </div>
        </Card>
      )}

      <Card className="p-4 space-y-4">
        <div className="flex items-center gap-2 text-xs text-stone-600 flex-wrap" data-testid="absen-office-info">
          <MapPin className="h-3.5 w-3.5 text-emerald-600" />
          <span>Lokasi kantor: <b>{office?.name || "—"}</b> · radius <b>{office?.radius_meters || 0}m</b></span>
          {office?.tz_name && (
            <span className="inline-flex items-center gap-1 ml-auto text-stone-700" data-testid="absen-tz-info">
              <Clock className="h-3 w-3" />
              <b>{office.tz_name}</b>
              <span className="font-mono">
                ({new Date().toLocaleTimeString("id-ID", { hour: "2-digit", minute: "2-digit", second: "2-digit", timeZone: office.tz_name })})
              </span>
            </span>
          )}
        </div>

        {/* Iter v30 — Face model status banner with retry */}
        {!blocked && (faError || !faReady) && (
          <div className={`p-3 text-xs border ${faError ? "bg-red-50 border-red-200" : "bg-blue-50 border-blue-200"}`} data-testid="face-model-status">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-2">
                {faError ? <AlertTriangle className="h-4 w-4 text-red-600" /> : <Clock className="h-4 w-4 text-blue-600 animate-pulse" />}
                <span className={faError ? "text-red-800 font-medium" : "text-blue-800"}>
                  {faError ? `Gagal memuat model wajah: ${faError}` : (statusMsg || "Memuat model pengenalan wajah…")}
                </span>
              </div>
              {faError && (
                <Button onClick={initFaceApi} className="!bg-blue-600 hover:!bg-blue-700 !text-xs" data-testid="retry-face-model">
                  <RefreshCw className="h-3.5 w-3.5" /> Coba muat ulang
                </Button>
              )}
            </div>
          </div>
        )}

        <div className="relative bg-stone-900 aspect-square max-w-xl mx-auto overflow-hidden">
          <video ref={videoRef} className="w-full h-full object-cover" muted autoPlay playsInline data-testid="absen-video" />
          <canvas ref={canvasRef} className="hidden" />
          {/* Iter v34d — Face-shaped oval guide overlay (#8). Replaces the
              previous perfect-circle. Aspect 3:4 with elliptical mask makes it
              easier to align face position. */}
          {stream && (
            <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
              <div
                className="border-2 border-white/60 shadow-[0_0_0_9999px_rgba(0,0,0,0.35)]"
                style={{ width: "62%", height: "82%", borderRadius: "50% / 60%" }}
                data-testid="face-guide-oval"
              />
            </div>
          )}
          {!stream && (
            <div className="absolute inset-0 flex items-center justify-center text-stone-300 text-xs">
              Kamera belum aktif
            </div>
          )}
        </div>
        <div className="flex flex-wrap gap-2 justify-center">
          {!stream ? (
            <Button onClick={startCam} disabled={!faReady} data-testid="absen-start-cam"><Camera className="h-3.5 w-3.5" /> Nyalakan Kamera</Button>
          ) : (
            <Button variant="outline" onClick={stopCam}>Matikan Kamera</Button>
          )}
          {needsFace && stream && (
            <Button onClick={setupFace} disabled={busy} data-testid="absen-setup-face">
              <ScanFace className="h-3.5 w-3.5" /> Simpan Wajah
            </Button>
          )}
          {!needsFace && stream && !today?.clock_in_ts && (
            <Button onClick={() => doClock("in")} disabled={busy} data-testid="absen-clock-in">
              <LogIn className="h-3.5 w-3.5" /> Clock-In
            </Button>
          )}
          {!needsFace && stream && today?.clock_in_ts && !today?.clock_out_ts && (
            <Button
              onClick={() => doClock("out")}
              disabled={busy || cooldownRemaining > 0 || onBreak}
              className="!bg-red-600 hover:!bg-red-700 disabled:!opacity-60"
              title={onBreak ? "Klik 'Lanjut Bekerja' dulu sebelum Clock-Out" : ""}
              data-testid="absen-clock-out"
            >
              <LogOut className="h-3.5 w-3.5" /> {cooldownRemaining > 0 ? `Tunggu ${cooldownDisplay}` : "Clock-Out"}
            </Button>
          )}
        </div>
        {/* Iter v34ci — Istirahat/Lanjut Bekerja bar. Terpisah dari kontrol kamera
            supaya tidak hilang saat kamera dinyalakan/dimatikan. Muncul selama
            sudah clock-in dan belum clock-out. Hilang setelah clock-out. */}
        {!needsFace && today?.clock_in_ts && !today?.clock_out_ts && (
          <div className="flex flex-wrap gap-2 justify-center items-center bg-stone-50 border border-stone-200 rounded-xl p-3" data-testid="break-bar">
            <span className="text-[11px] uppercase tracking-widest text-stone-500 font-semibold mr-1">Status Kerja:</span>
            {onBreak ? (
              <Button
                onClick={() => doBreakAction("end")}
                disabled={busy}
                className="!bg-emerald-600 hover:!bg-emerald-700"
                data-testid="absen-break-end"
              >
                <LogIn className="h-3.5 w-3.5" /> Lanjut Bekerja
              </Button>
            ) : canShowBreakBtn ? (
              <Button
                onClick={() => doBreakAction("start")}
                disabled={busy || breakLimitReached}
                variant="outline"
                className="!border-amber-400 !text-amber-700 hover:!bg-amber-50"
                title={breakLimitReached ? `Sudah mencapai batas ${breakConfig.max_breaks_per_day}x istirahat hari ini` : "Mulai istirahat"}
                data-testid="absen-break-start"
              >
                <Coffee className="h-3.5 w-3.5" /> Mulai Istirahat
              </Button>
            ) : (
              <span className="text-[11px] text-stone-500 italic" data-testid="break-not-yet-hint">
                {(() => {
                  const need = Math.max(0, (breakConfig.min_hours_after_clockin || 8) - hoursSinceClockIn);
                  const mins = Math.ceil(need * 60);
                  if (need <= 0) return "";
                  return `Tombol Istirahat aktif ${need >= 1 ? need.toFixed(1) + " jam" : mins + " menit"} lagi setelah clock-in.`;
                })()}
              </span>
            )}
            {!onBreak && (today?.breaks || []).length > 0 && (
              <span className="text-[10px] text-stone-500 ml-auto" data-testid="break-count-inline">
                Sudah istirahat {(today?.breaks || []).length}× · {Math.round(today?.break_minutes || 0)} menit
              </span>
            )}
          </div>
        )}
        {statusMsg && <div className="text-center text-xs text-stone-500 italic">{statusMsg}</div>}

        {/* Iter v34al — Cooldown banner setelah clock-in (anti double-tap). */}
        {cooldownRemaining > 0 && today?.clock_in_ts && !today?.clock_out_ts && (
          <div className="bg-amber-50 border border-amber-200 p-2.5 text-xs text-amber-800 text-center" data-testid="clock-cooldown-banner">
            <b>Jeda Minimum:</b> tombol Clock-Out aktif dalam <b>{cooldownDisplay}</b>. Tunggu setidaknya 30 menit setelah clock-in.
          </div>
        )}

        {/* Iter v34ao (#2) — Banner status istirahat (visual jelas untuk karyawan & supervisor). */}
        {onBreak && activeBreak && (
          <div className="bg-amber-100 border-l-4 border-amber-500 p-3 flex items-center gap-2 text-amber-900" data-testid="break-active-banner">
            <Coffee className="h-4 w-4 shrink-0" />
            <div className="text-xs">
              <b>SEDANG ISTIRAHAT</b> · dimulai {new Date(activeBreak.start_ts).toLocaleTimeString("id-ID", { hour: "2-digit", minute: "2-digit" })} · durasi berjalan {Math.max(0, Math.floor((nowTick - new Date(activeBreak.start_ts).getTime()) / 60000))} menit. Klik <b>Lanjut Bekerja</b> untuk melanjutkan.
            </div>
          </div>
        )}
        {!onBreak && (today?.breaks || []).length > 0 && !today?.clock_out_ts && (
          <div className="text-[11px] text-stone-500 text-center" data-testid="break-count-info">
            Istirahat hari ini: {(today?.breaks || []).length}× · total {Math.round(today?.break_minutes || 0)} menit
            {(breakConfig.max_breaks_per_day || 0) > 0 && ` / batas ${breakConfig.max_breaks_per_day}×`}
          </div>
        )}

        {/* Iter v34al — Info perangkat low-end (agar user paham mengapa lambat & apa yang bisa dibantu). */}
        {IS_LOW_END && (
          <div className="bg-blue-50 border border-blue-200 p-2.5 text-[11px] text-blue-800 text-center" data-testid="low-end-hint">
            Mode ringan aktif · resolusi kamera diturunkan untuk perangkat kelas menengah. Pastikan pencahayaan cukup & tutup aplikasi lain agar tidak lag.
          </div>
        )}

        {/* Iter v28 Phase 3 — face verification failure banner with override after 3 fails */}
        {(faceFailState.in || faceFailState.out) && (() => {
          const k = faceFailState.in ? "in" : "out";
          const st = faceFailState[k];
          return (
            <div className="bg-red-50 border border-red-200 p-3 rounded-sm" data-testid="face-fail-banner">
              <div className="flex items-start gap-2">
                <AlertTriangle className="h-4 w-4 text-red-600 mt-0.5" />
                <div className="flex-1 text-xs">
                  <div className="font-semibold text-red-800">Verifikasi wajah gagal ({st.attempts}/{st.max})</div>
                  <div className="text-red-700 mt-0.5">Jarak descriptor: <span className="font-mono">{st.dist}</span> (max {(0.4).toFixed(2)}). Pastikan wajah terang & menghadap kamera lurus.</div>
                  {st.canOverride && (
                    <div className="mt-2 flex flex-wrap gap-2">
                      <Button onClick={() => doClock(k, true)} disabled={busy} className="!bg-amber-600 hover:!bg-amber-700 !text-xs" data-testid={`absen-override-${k}`}>
                        <AlertTriangle className="h-3.5 w-3.5" /> Lapor Supervisor (Override)
                      </Button>
                      <Button variant="outline" onClick={() => setFaceFailState((s) => ({ ...s, [k]: null }))} className="!text-xs">Tutup</Button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })()}
      </Card>

      <Card className="p-4 space-y-3">
        <h3 className="font-display text-lg font-semibold text-stone-900 flex items-center gap-2"><Clock className="h-4 w-4 text-terra-600" /> Status Hari Ini</h3>
        {today ? (
          <div className="grid grid-cols-2 gap-3 text-xs">
            <div><div className="text-stone-500 uppercase tracking-widest mb-1">Clock-In</div><div className="font-mono text-stone-900">{(today.clock_in_local_ts || today.clock_in_ts)?.slice(11,16) || "—"}</div></div>
            <div><div className="text-stone-500 uppercase tracking-widest mb-1">Clock-Out</div><div className="font-mono text-stone-900">{(today.clock_out_local_ts || today.clock_out_ts)?.slice(11,16) || "—"}</div></div>
            <div><div className="text-stone-500 uppercase tracking-widest mb-1">Jarak Kantor</div><div className="font-mono">{today.clock_in_distance_m || 0}m</div></div>
            <div><div className="text-stone-500 uppercase tracking-widest mb-1">Status</div><Badge tone={today.status === "completed" ? "emerald" : (today.status === "pending_approval" ? "amber" : "stone")}>{today.status}</Badge></div>
          </div>
        ) : <p className="text-xs text-stone-400 italic">Belum ada absensi hari ini.</p>}
      </Card>

      {summary && (
        <div className="grid grid-cols-2 gap-3">
          <Card className="p-4 text-center">
            <div className="text-[10px] uppercase tracking-widest text-stone-500">Minggu Ini</div>
            <div className="text-2xl font-display font-bold text-terra-700 mt-1">{summary.week.days_present} hari</div>
            <div className="text-xs text-stone-600">{summary.week.work_hours.toFixed(1)} jam · Lembur {summary.week.overtime_hours.toFixed(1)}j</div>
          </Card>
          <Card className="p-4 text-center">
            <div className="text-[10px] uppercase tracking-widest text-stone-500">Bulan Ini</div>
            <div className="text-2xl font-display font-bold text-emerald-700 mt-1">{summary.month.days_present} hari</div>
            <div className="text-xs text-stone-600">{summary.month.work_hours.toFixed(1)} jam · Lembur {summary.month.overtime_hours.toFixed(1)}j</div>
          </Card>
        </div>
      )}

      {/* Iter v34n (#1) — Libur Nasional & Cuti Bersama (3 hari kerja terdekat ke depan) */}
      {(() => {
        const today = new Date().toISOString().slice(0, 10);
        const upcoming = (holidays || []).filter((h) => h.date >= today).slice(0, 5);
        if (upcoming.length === 0) return null;
        return (
          <Card className="p-4">
            <h3 className="font-display font-semibold text-sm text-stone-900 flex items-center gap-2 mb-3">
              <CalendarCheck className="h-4 w-4 text-red-600" />
              Libur Nasional Terdekat
            </h3>
            <div className="space-y-2">
              {upcoming.map((h) => {
                const d = new Date(h.date);
                const dayLabel = d.toLocaleDateString("id-ID", { weekday: "long" });
                const dateLabel = d.toLocaleDateString("id-ID", { day: "2-digit", month: "long", year: "numeric" });
                return (
                  <div key={h.id} className="flex items-center justify-between gap-3 border-l-2 border-red-400 pl-3 py-1" data-testid={`karyawan-holiday-${h.id}`}>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-stone-900 truncate">{h.name}</p>
                      <p className="text-[11px] text-stone-500">{dayLabel}, {dateLabel}</p>
                    </div>
                    {h.is_cuti_bersama && <Badge tone="amber" className="!text-[10px] shrink-0">Cuti Bersama</Badge>}
                  </div>
                );
              })}
            </div>
          </Card>
        );
      })()}
    </div>
    </div>
  );
}
